import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAdminAuth } from "@/hooks/use-admin-auth";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Loader2,
  LogOut,
  Trash2,
  Copy,
  Settings,
  X,
  CheckCircle2,
  AlertCircle,
  RotateCw,
  FolderInput,
  Pencil,
} from "lucide-react";
import { TagChipInput } from "@/components/admin/TagChipInput";
import { PngDropzone } from "@/components/admin/PngDropzone";
import { CategoryManagerDialog } from "@/components/admin/CategoryManagerDialog";
import { EditImageDialog } from "@/components/admin/EditImageDialog";
import { CharacterQuickTags } from "@/components/admin/CharacterQuickTags";
import { convertImage, extOf } from "@/lib/image-utils";

interface Category {
  id: string;
  name: string;
  slug: string;
  sort_order: number;
}

interface ImageRow {
  id: string;
  category_id: string;
  file_name: string;
  original_name: string;
  storage_path: string;
  public_url: string;
  tags: string[];
  audience: string[];
  created_at: string;
  is_active: boolean;
}

type PendingStatus = "idle" | "uploading" | "success" | "error";
type FormatOption = "png" | "jpg" | "both";

interface Pending {
  id: string;
  file: File;
  previewUrl: string;
  displayName: string;
  targetName: string; // preview label, real name resolved at upload
  status: PendingStatus;
  errorMsg?: string;
}

const BUCKET = "image-library";
const SIGNED_TTL = 60 * 60; // 1 hour, refreshed on load

const CHECKER_STYLE = {
  backgroundImage:
    "linear-gradient(45deg, hsl(var(--muted)) 25%, transparent 25%), linear-gradient(-45deg, hsl(var(--muted)) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, hsl(var(--muted)) 75%), linear-gradient(-45deg, transparent 75%, hsl(var(--muted)) 75%)",
  backgroundSize: "16px 16px",
  backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0",
} as const;

const AdminLibrary = () => {
  const navigate = useNavigate();
  const { session, isAdmin, loading } = useAdminAuth();
  const [categories, setCategories] = useState<Category[]>([]);
  const [activeCat, setActiveCat] = useState<string | null>(null);
  const [images, setImages] = useState<ImageRow[]>([]);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [audience, setAudience] = useState<string[]>(["parents", "teachers"]);
  const [format, setFormat] = useState<FormatOption>("both");
  const [pending, setPending] = useState<Pending[]>([]);
  const [catMgrOpen, setCatMgrOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState<string>("");
  const [editTarget, setEditTarget] = useState<ImageRow | null>(null);

  useEffect(() => {
    if (loading) return;
    if (!session) navigate("/admin/login", { replace: true });
    else if (!isAdmin) navigate("/image-library", { replace: true });
  }, [loading, session, isAdmin, navigate]);

  const loadCategories = useCallback(async () => {
    const { data, error } = await supabase
      .from("categories")
      .select("*")
      .order("sort_order", { ascending: true });
    if (error) return toast.error(error.message);
    setCategories(data ?? []);
    if (!activeCat && data && data.length) setActiveCat(data[0].id);
  }, [activeCat]);

  const refreshSigned = useCallback(async (rows: ImageRow[]) => {
    if (!rows.length) return setSignedUrls({});
    const paths = rows.map((r) => r.storage_path);
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrls(paths, SIGNED_TTL);
    if (error) return;
    const m: Record<string, string> = {};
    (data ?? []).forEach((d, i) => {
      if (d.signedUrl) m[rows[i].id] = d.signedUrl;
    });
    setSignedUrls(m);
  }, []);

  const loadImages = useCallback(
    async (categoryId: string) => {
      const { data, error } = await supabase
        .from("images")
        .select("*")
        .eq("category_id", categoryId)
        .order("file_name", { ascending: true });
      if (error) return toast.error(error.message);
      const rows = (data ?? []) as ImageRow[];
      setImages(rows);
      setSelected(new Set());
      void refreshSigned(rows);
    },
    [refreshSigned]
  );

  useEffect(() => {
    if (isAdmin) loadCategories();
  }, [isAdmin, loadCategories]);

  useEffect(() => {
    if (activeCat) loadImages(activeCat);
  }, [activeCat, loadImages]);

  const activeCatObj = useMemo(
    () => categories.find((c) => c.id === activeCat),
    [categories, activeCat]
  );

  const nextSeed = useMemo(() => {
    if (!activeCatObj) return 1;
    let max = 0;
    const re = new RegExp(`^${activeCatObj.slug}_(\\d+)\\.(png|jpe?g)$`, "i");
    for (const im of images) {
      const m = im.file_name.match(re);
      if (m) max = Math.max(max, parseInt(m[1], 10));
    }
    return max + 1;
  }, [images, activeCatObj]);

  const genId = () =>
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random()}`;

  const addPending = (files: File[]) => {
    if (!activeCatObj) return toast.error("請先選擇分類");
    setPending((prev) => {
      const notDone = prev.filter((p) => p.status !== "success");
      const startN = nextSeed + notDone.length;
      const additions: Pending[] = files.map((f, i) => ({
        id: genId(),
        file: f,
        previewUrl: URL.createObjectURL(f),
        displayName: f.name.replace(/\.(png|jpe?g)$/i, ""),
        targetName: `${activeCatObj.slug}_${String(startN + i).padStart(3, "0")}`,
        status: "idle",
      }));
      return [...notDone, ...additions];
    });
  };

  const removePending = (id: string) => {
    setPending((prev) => {
      const item = prev.find((p) => p.id === id);
      if (item) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  };

  const clearPending = () => {
    pending.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    setPending([]);
  };

  useEffect(() => {
    if (!activeCatObj) return;
    setPending((prev) =>
      prev.map((p, i) => ({
        ...p,
        targetName: `${activeCatObj.slug}_${String(nextSeed + i).padStart(3, "0")}`,
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCatObj?.slug]);

  // Upload one file with a specific extension. Retries filename on conflict.
  const uploadFileAs = async (
    p: Pending,
    ext: "png" | "jpg",
    startNum: number
  ): Promise<{ ok: boolean; error?: string }> => {
    if (!activeCatObj) return { ok: false, error: "無分類" };
    const src = p.file;
    const finalFile: File = extOf(src) === ext ? src : await convertImage(src, ext);
    let attempt = 0;
    let num = startNum;
    while (attempt < 5) {
      attempt++;
      const fileName = `${activeCatObj.slug}_${String(num).padStart(3, "0")}.${ext}`;
      const storagePath = `${activeCatObj.slug}/${fileName}`;
      const { error: upErr } = await supabase.storage
        .from(BUCKET)
        .upload(storagePath, finalFile, {
          contentType: ext === "png" ? "image/png" : "image/jpeg",
          upsert: false,
        });
      if (upErr) {
        if (upErr.message.toLowerCase().includes("exist")) {
          num++;
          continue;
        }
        return { ok: false, error: upErr.message };
      }
      const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);
      const { error: insErr } = await supabase.from("images").insert({
        category_id: activeCatObj.id,
        original_name: p.displayName.trim() || p.file.name,
        file_name: fileName,
        storage_path: storagePath,
        public_url: pub.publicUrl,
        tags,
        audience,
        uploaded_by: session?.user.id,
      });
      if (insErr) {
        await supabase.storage.from(BUCKET).remove([storagePath]);
        if (insErr.code === "23505") {
          num++;
          continue;
        }
        return { ok: false, error: insErr.message };
      }
      return { ok: true };
    }
    return { ok: false, error: "檔名衝突，請重試" };
  };

  const uploadOne = async (p: Pending): Promise<void> => {
    setPending((prev) =>
      prev.map((x) =>
        x.id === p.id ? { ...x, status: "uploading", errorMsg: undefined } : x
      )
    );
    // Fetch latest max from DB to avoid races
    const { data: latest } = await supabase
      .from("images")
      .select("file_name")
      .eq("category_id", activeCat!);
    let max = 0;
    const re = new RegExp(`^${activeCatObj!.slug}_(\\d+)\\.(png|jpe?g)$`, "i");
    for (const row of latest ?? []) {
      const m = row.file_name.match(re);
      if (m) max = Math.max(max, parseInt(m[1], 10));
    }
    const startNum = max + 1;

    const exts: ("png" | "jpg")[] =
      format === "both" ? ["png", "jpg"] : [format];
    for (const ext of exts) {
      const res = await uploadFileAs(p, ext, startNum);
      if (!res.ok) {
        setPending((prev) =>
          prev.map((x) =>
            x.id === p.id ? { ...x, status: "error", errorMsg: res.error } : x
          )
        );
        return;
      }
    }
    setPending((prev) =>
      prev.map((x) => (x.id === p.id ? { ...x, status: "success" } : x))
    );
  };

  const handleBatchUpload = async () => {
    const queue = pending.filter((p) => p.status !== "success");
    if (!queue.length || !activeCat) return;
    setUploading(true);
    for (const p of queue) {
      await uploadOne(p);
    }
    setUploading(false);
    if (activeCat) await loadImages(activeCat);
    setPending((prev) => {
      const ok = prev.filter((p) => p.status === "success").length;
      const fail = prev.filter((p) => p.status === "error").length;
      if (ok) toast.success(`已上傳 ${ok} 張${fail ? `，${fail} 張失敗` : ""}`);
      else if (fail) toast.error(`${fail} 張上傳失敗`);
      prev.filter((p) => p.status === "success").forEach((p) => URL.revokeObjectURL(p.previewUrl));
      return prev.filter((p) => p.status !== "success");
    });
  };

  const retryOne = async (id: string) => {
    const p = pending.find((x) => x.id === id);
    if (!p) return;
    await uploadOne(p);
    if (activeCat) await loadImages(activeCat);
    setPending((prev) => {
      const item = prev.find((x) => x.id === id);
      if (item?.status === "success") {
        URL.revokeObjectURL(item.previewUrl);
        return prev.filter((x) => x.id !== id);
      }
      return prev;
    });
  };

  const handleDelete = async (img: ImageRow) => {
    if (!confirm(`確定刪除 ${img.file_name}？`)) return;
    await supabase.storage.from(BUCKET).remove([img.storage_path]);
    const { error } = await supabase.from("images").delete().eq("id", img.id);
    if (error) return toast.error(error.message);
    toast.success("已刪除");
    if (activeCat) loadImages(activeCat);
  };

  const copyUrl = async (img: ImageRow) => {
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(img.storage_path, 60 * 60 * 24 * 30);
    if (error) return toast.error(error.message);
    await navigator.clipboard.writeText(data.signedUrl);
    toast.success("已複製連結（有效 30 天）");
  };

  const toggleSelected = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === images.length) setSelected(new Set());
    else setSelected(new Set(images.map((i) => i.id)));
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!confirm(`刪除 ${ids.length} 張圖片？此動作無法復原。`)) return;
    const selectedImgs = images.filter((i) => ids.includes(i.id));
    await supabase.storage.from(BUCKET).remove(selectedImgs.map((i) => i.storage_path));
    const { error } = await supabase.from("images").delete().in("id", ids);
    if (error) return toast.error(error.message);
    toast.success(`已刪除 ${ids.length} 張`);
    setSelected(new Set());
    if (activeCat) loadImages(activeCat);
  };

  const bulkMove = async () => {
    const ids = Array.from(selected);
    if (!ids.length || !moveTarget || moveTarget === activeCat) return;
    const { error } = await supabase
      .from("images")
      .update({ category_id: moveTarget })
      .in("id", ids);
    if (error) return toast.error(error.message);
    toast.success(`已移動 ${ids.length} 張`);
    setMoveOpen(false);
    setMoveTarget("");
    setSelected(new Set());
    if (activeCat) loadImages(activeCat);
  };

  const toggleActive = async (img: ImageRow, next: boolean) => {
    setImages((prev) => prev.map((i) => (i.id === img.id ? { ...i, is_active: next } : i)));
    const { error } = await supabase
      .from("images")
      .update({ is_active: next })
      .eq("id", img.id);
    if (error) {
      toast.error(error.message);
      setImages((prev) => prev.map((i) => (i.id === img.id ? { ...i, is_active: !next } : i)));
      return;
    }
    toast.success(next ? "已啟用" : "已停用");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const errCount = pending.filter((p) => p.status === "error").length;
  const readyCount = pending.filter((p) => p.status === "idle" || p.status === "error").length;
  const formatSuffixHint =
    format === "both" ? ".png + .jpg" : format === "png" ? ".png" : ".jpg";

  return (
    <>
      <Helmet>
        <title>後台管理 · Queebi 圖庫</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <main className="min-h-screen bg-background pb-20">
        <header className="border-b border-border bg-card">
          <div className="container flex h-14 items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="font-heading text-lg font-bold">Queebi 圖庫 · 後台</h1>
              <span className="text-xs text-muted-foreground">{session?.user.email}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="default" size="sm" onClick={() => setCatMgrOpen(true)}>
                <Settings className="h-4 w-4 mr-1" /> + 新增/管理分類
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigate("/image-library")}>
                前台檢視
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  await supabase.auth.signOut();
                  navigate("/admin/login");
                }}
              >
                <LogOut className="h-4 w-4 mr-1" /> 登出
              </Button>
            </div>
          </div>
        </header>

        <div className="container grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 py-6">
          <aside className="space-y-1">
            <Label className="text-xs uppercase text-muted-foreground">快速切換</Label>
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveCat(c.id)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                  activeCat === c.id
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-muted"
                }`}
              >
                <div className="font-medium">{c.name}</div>
                <div className="text-[11px] opacity-70">{c.slug}</div>
              </button>
            ))}
          </aside>

          <section className="space-y-6">
            {/* Upload panel */}
            <div className="bg-card border border-border rounded-xl p-5 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>分類</Label>
                  <Select value={activeCat ?? ""} onValueChange={setActiveCat}>
                    <SelectTrigger>
                      <SelectValue placeholder="選擇分類" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name} · <span className="font-mono text-xs">{c.slug}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>檔案格式</Label>
                  <Select value={format} onValueChange={(v) => setFormat(v as FormatOption)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="both">同時產生 PNG + JPG</SelectItem>
                      <SelectItem value="png">僅 PNG</SelectItem>
                      <SelectItem value="jpg">僅 JPG</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>適用對象</Label>
                  <div className="flex gap-4 pt-2">
                    {[
                      { key: "parents", label: "家長" },
                      { key: "teachers", label: "老師" },
                    ].map((a) => (
                      <label key={a.key} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox
                          checked={audience.includes(a.key)}
                          onCheckedChange={(v) =>
                            setAudience((prev) =>
                              v
                                ? Array.from(new Set([...prev, a.key]))
                                : prev.filter((x) => x !== a.key)
                            )
                          }
                        />
                        <span>{a.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>標籤（供搜尋使用）</Label>
                <CharacterQuickTags tags={tags} onChange={setTags} />
                <TagChipInput
                  tags={tags}
                  onChange={setTags}
                  placeholder="輸入標籤後按 Enter 或逗號"
                />
              </div>

              <div className="rounded-lg bg-muted/40 border border-dashed border-border px-4 py-2 text-xs text-muted-foreground">
                <strong className="text-foreground">上傳限制：</strong>單張最大 1000×1000 像素、8 MB 以內；
                支援 PNG / JPG。上傳後會依分類自動命名為 <span className="font-mono">{activeCatObj?.slug ?? "slug"}_001{formatSuffixHint}</span>。
              </div>

              <PngDropzone onFiles={addPending} disabled={!activeCat || uploading} />

              {pending.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <Label className="text-sm">
                      預覽（{pending.length}）
                      {errCount > 0 && (
                        <span className="ml-2 text-destructive">— {errCount} 張失敗</span>
                      )}
                    </Label>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={clearPending} disabled={uploading}>
                        清除
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleBatchUpload}
                        disabled={uploading || readyCount === 0}
                      >
                        {uploading && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                        上傳 {readyCount} 張
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {pending.map((p) => (
                      <div
                        key={p.id}
                        className={`relative border rounded-lg overflow-hidden ${
                          p.status === "error"
                            ? "border-destructive/60 bg-destructive/5"
                            : p.status === "success"
                            ? "border-green-500/60 bg-green-500/5"
                            : "border-border bg-muted/30"
                        }`}
                      >
                        <div className="aspect-square" style={CHECKER_STYLE}>
                          <img
                            src={p.previewUrl}
                            alt={p.file.name}
                            className="w-full h-full object-contain"
                          />
                        </div>

                        <div className="absolute top-1 left-1">
                          {p.status === "uploading" && (
                            <div className="bg-background/90 rounded-full p-1">
                              <Loader2 className="h-3 w-3 animate-spin" />
                            </div>
                          )}
                          {p.status === "success" && (
                            <div className="bg-background/90 rounded-full p-1">
                              <CheckCircle2 className="h-3 w-3 text-green-600" />
                            </div>
                          )}
                          {p.status === "error" && (
                            <div className="bg-background/90 rounded-full p-1">
                              <AlertCircle className="h-3 w-3 text-destructive" />
                            </div>
                          )}
                        </div>

                        <button
                          onClick={() => removePending(p.id)}
                          className="absolute top-1 right-1 bg-background/90 rounded-full p-1 hover:bg-destructive hover:text-destructive-foreground"
                          aria-label="移除"
                          disabled={p.status === "uploading"}
                        >
                          <X className="h-3 w-3" />
                        </button>

                        <div className="p-2 space-y-1">
                          <Input
                            value={p.displayName}
                            onChange={(e) =>
                              setPending((prev) =>
                                prev.map((x) =>
                                  x.id === p.id ? { ...x, displayName: e.target.value } : x
                                )
                              )
                            }
                            placeholder="搜尋用名稱"
                            disabled={p.status === "uploading" || p.status === "success"}
                            className="h-8 text-sm"
                          />
                          <div className="text-[10px] font-mono text-muted-foreground truncate">
                            → {p.targetName}
                            {format === "both" ? ".png+.jpg" : `.${format}`}
                          </div>
                          {p.status === "error" && (
                            <>
                              <div className="text-[10px] text-destructive truncate" title={p.errorMsg}>
                                {p.errorMsg}
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => retryOne(p.id)}
                                className="h-6 text-[11px] w-full mt-1"
                                disabled={uploading}
                              >
                                <RotateCw className="h-3 w-3 mr-1" /> 重試
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Existing images */}
            <div>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <Label className="text-sm">
                  {activeCatObj?.name} — 共 {images.length} 張
                </Label>
                {images.length > 0 && (
                  <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                    <Checkbox
                      checked={selected.size === images.length && images.length > 0}
                      onCheckedChange={toggleSelectAll}
                    />
                    全選
                  </label>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {images.map((img) => {
                  const isSel = selected.has(img.id);
                  const inactive = !img.is_active;
                  const src = signedUrls[img.id];
                  return (
                    <div
                      key={img.id}
                      className={`bg-card border rounded-lg overflow-hidden relative transition-all ${
                        isSel ? "border-primary ring-2 ring-primary/40" : "border-border"
                      } ${inactive ? "opacity-60" : ""}`}
                    >
                      <div className="absolute top-2 left-2 z-10 bg-background/90 rounded p-1">
                        <Checkbox
                          checked={isSel}
                          onCheckedChange={() => toggleSelected(img.id)}
                          aria-label="選取"
                        />
                      </div>
                      {inactive && (
                        <Badge variant="secondary" className="absolute top-2 right-2 z-10 text-[10px]">
                          已停用
                        </Badge>
                      )}
                      <div className="aspect-square" style={CHECKER_STYLE}>
                        {src ? (
                          <img
                            src={src}
                            alt={img.original_name || img.file_name}
                            className="w-full h-full object-contain"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="p-2 space-y-1">
                        <div className="text-xs truncate" title={img.original_name}>
                          {img.original_name}
                        </div>
                        <div className="text-[10px] font-mono text-muted-foreground truncate">
                          {img.file_name}
                        </div>
                        {img.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {img.tags.slice(0, 3).map((t) => (
                              <Badge key={t} variant="secondary" className="text-[10px]">
                                {t}
                              </Badge>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center justify-between pt-1">
                          <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer">
                            <Switch
                              checked={img.is_active}
                              onCheckedChange={(v) => toggleActive(img, v)}
                            />
                            {img.is_active ? "啟用" : "停用"}
                          </label>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditTarget(img)}
                              className="h-7 px-2 text-xs"
                              aria-label="編輯"
                            >
                              <Pencil className="h-3 w-3 mr-1" /> 編輯
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => copyUrl(img)}
                              className="h-7 px-2 text-xs"
                              aria-label="複製連結"
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(img)}
                              className="h-7 px-2 text-xs text-destructive"
                              aria-label="刪除"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {images.length === 0 && (
                  <div className="col-span-full text-center text-sm text-muted-foreground py-12">
                    此分類尚無圖片。
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>

        {selected.size > 0 && (
          <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-foreground text-background rounded-full shadow-lg px-4 py-2 flex items-center gap-3">
            <span className="text-sm font-medium">已選 {selected.size} 張</span>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 text-background hover:bg-background/10 hover:text-background"
              onClick={() => setMoveOpen(true)}
            >
              <FolderInput className="h-4 w-4 mr-1" /> 移動
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 text-background hover:bg-destructive hover:text-destructive-foreground"
              onClick={bulkDelete}
            >
              <Trash2 className="h-4 w-4 mr-1" /> 刪除
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 text-background hover:bg-background/10 hover:text-background"
              onClick={() => setSelected(new Set())}
              aria-label="取消選取"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        <CategoryManagerDialog
          open={catMgrOpen}
          onOpenChange={setCatMgrOpen}
          onChanged={loadCategories}
        />

        <EditImageDialog
          image={editTarget}
          open={!!editTarget}
          onOpenChange={(v) => !v && setEditTarget(null)}
          onSaved={() => activeCat && loadImages(activeCat)}
        />

        <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>移動 {selected.size} 張圖片</DialogTitle>
              <DialogDescription>
                檔名維持不變（現有連結仍可使用），但分類 slug 可能不同。
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label>目標分類</Label>
              <Select value={moveTarget} onValueChange={setMoveTarget}>
                <SelectTrigger>
                  <SelectValue placeholder="選擇分類" />
                </SelectTrigger>
                <SelectContent>
                  {categories
                    .filter((c) => c.id !== activeCat)
                    .map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} · <span className="font-mono text-xs">{c.slug}</span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setMoveOpen(false)}>
                取消
              </Button>
              <Button onClick={bulkMove} disabled={!moveTarget}>
                移動
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </>
  );
};

export default AdminLibrary;
